import { computed, inject } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { EncryptionService } from '@nbx/core/encryption';
import { StoreResetRegistry, withReset, withResource, withSaver } from '@nbx/core/state';
import { LocalStorageService } from '@nbx/core/storage';

import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { APP_PATHS_SLASH } from '@app/core/enums/paths/app.paths';
import { AuthResponse, AuthTokenRequest } from '@interfaces/auth.interface';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { AuthService } from '@services/auth.service';

export interface AuthCredentials {
  email: string;
  password: string;
  remember: boolean;
}

interface AuthStateModel {
  accessToken: string;
  refreshToken: string;
  hostName: string | null;
  hostCodeName: string | null;
}

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const HOST_CODE_NAME_KEY = 'host_code_name';
const EMAIL_KEY = 'email';
const PASSWORD_KEY = 'password';
const REDIRECT_URL_KEY = 'redirectUrl';

function readStorageValue(key: string): string {
  return localStorage.getItem(key) ?? '';
}

function persistTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

function clearStoredTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * True when a JWT is missing, unparseable, or within a 30-second pre-expiry buffer,
 * so guards proactively refresh before the server rejects the token.
 */
function isJwtExpired(token: string): boolean {
  if (!token) {
    return true;
  }
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return true;
    }
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const payload = JSON.parse(atob(padded)) as { exp?: number };
    if (!payload.exp) {
      return false;
    }
    const bufferMs = 30_000;
    return Date.now() >= payload.exp * 1000 - bufferMs;
  } catch {
    return true;
  }
}

/**
 * Auth/session store: tokens and host identity seed synchronously from localStorage (SPA,
 * no SSR hazard). `host` is a resource gated on `accessToken` (idle while logged out);
 * `login`/`validateHost` are savers. `refresh()` stays an Observable for the interceptor
 * and guards to compose directly (convention-10 exception).
 */
export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState<AuthStateModel>(() => ({
    accessToken: readStorageValue(ACCESS_TOKEN_KEY),
    refreshToken: readStorageValue(REFRESH_TOKEN_KEY),
    hostName: null,
    hostCodeName: localStorage.getItem(HOST_CODE_NAME_KEY),
  })),
  withProps(() => ({
    _authService: inject(AuthService),
    _router: inject(Router),
    _localStorage: inject(LocalStorageService),
    _encryption: inject(EncryptionService),
    _resetRegistry: inject(StoreResetRegistry),
  })),
  withResource(
    'host',
    store =>
      rxResource({
        params: () => (store.accessToken() ? {} : undefined),
        stream: () => store._authService.getHost(),
      }),
    { fallback: null }
  ),
  withComputed(store => ({
    isAccessTokenExpired: computed(() => isJwtExpired(store.accessToken())),
    currentUserRole: computed(() => store.host()?.role ?? null),
  })),
  withSaver(
    'login',
    store => (req: AuthTokenRequest & { remember: boolean }) => store._authService.login(req),
    {
      invalidates: ['host'],
      onSuccess: (store, response, req) => {
        patchState(store, {
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
        });
        persistTokens(response.accessToken, response.refreshToken);
        store._localStorage.setItem(EMAIL_KEY, req.remember ? req.username : '');
        store._localStorage.setItem(
          PASSWORD_KEY,
          req.remember ? store._encryption.encrypt(req.password) : ''
        );
        const returnUrl = localStorage.getItem(REDIRECT_URL_KEY);
        localStorage.removeItem(REDIRECT_URL_KEY);
        void store._router.navigate([returnUrl ?? APP_PATHS_SLASH.HOME]);
      },
    }
  ),
  withSaver(
    'validateHost',
    store => (externalCodeName: string) => store._authService.validateHost(externalCodeName),
    {
      onSuccess: (store, response, externalCodeName) => {
        patchState(store, { hostName: response.name, hostCodeName: externalCodeName });
        localStorage.setItem(HOST_CODE_NAME_KEY, externalCodeName);
      },
    }
  ),
  withMethods(store => {
    /** Re-reads localStorage so external token removal (e.g. DevTools) is picked up. */
    function syncTokensFromStorage(): void {
      const storedAccess = readStorageValue(ACCESS_TOKEN_KEY);
      const storedRefresh = readStorageValue(REFRESH_TOKEN_KEY);
      if (storedAccess !== store.accessToken() || storedRefresh !== store.refreshToken()) {
        patchState(store, { accessToken: storedAccess, refreshToken: storedRefresh });
      }
    }

    return {
      isAuthenticated(): boolean {
        syncTokensFromStorage();
        return !!store.accessToken();
      },
      hasRefreshToken(): boolean {
        syncTokensFromStorage();
        return !!store.refreshToken();
      },
      clearHost(): void {
        patchState(store, { hostName: null, hostCodeName: null });
        localStorage.removeItem(HOST_CODE_NAME_KEY);
      },
      logout(redirectUrl?: string): void {
        const hostCodeName = store.hostCodeName() ?? localStorage.getItem(HOST_CODE_NAME_KEY);
        store._resetRegistry.resetAll();
        clearStoredTokens();
        localStorage.removeItem(HOST_CODE_NAME_KEY);
        patchState(store, {
          accessToken: '',
          refreshToken: '',
          hostName: null,
          hostCodeName: null,
        });
        if (redirectUrl) {
          localStorage.setItem(REDIRECT_URL_KEY, redirectUrl);
        }
        store._router.navigate([APP_PATHS_SLASH.SIGN_IN], {
          queryParams: hostCodeName ? { externalCodeName: hostCodeName } : undefined,
        });
      },
      /** Composable refresh kept as an Observable for the interceptor/guards to pipe. */
      refresh(): Observable<boolean> {
        const refreshToken = store.refreshToken();
        if (!refreshToken) {
          return of(false);
        }
        return store._authService.refreshToken(refreshToken).pipe(
          tap((response: AuthResponse) => {
            patchState(store, {
              accessToken: response.accessToken,
              refreshToken: response.refreshToken,
            });
            persistTokens(response.accessToken, response.refreshToken);
          }),
          map(() => true),
          catchError(() => of(false))
        );
      },
      loadLoginCredentials(): AuthCredentials {
        const email = store._localStorage.getItem(EMAIL_KEY) ?? '';
        const encryptedPassword = store._localStorage.getItem(PASSWORD_KEY) ?? '';
        let password = '';
        if (encryptedPassword) {
          try {
            password = store._encryption.decrypt(encryptedPassword);
          } catch {
            store._localStorage.setItem(PASSWORD_KEY, '');
          }
        }
        return { email, password, remember: !!email };
      },
    };
  }),
  withReset({ global: false })
);
